import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useFanStore } from '@stores/fan.store';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useStorage } from '@vueuse/core';
import { useAccountFetcher } from '@api/account.api';
import { useArtistStore } from '@stores/artist.store';

export const useAccountStore = defineStore('account', () => {
  const router = useRouter();
  const route = useRoute();
  const cookies = useCookies(['f_uauth']);

  const artistStore = useArtistStore();
  const fanStore = useFanStore();

  const guestEmail = useStorage('emailFromQuery', '', sessionStorage);

  const authToken = computed<string | undefined>(() => cookies.get('f_uauth') || undefined);
  const isAuthenticated = computed(() => !!authToken.value);
  const tokenInQuery = computed(() => route.query?.token as string);

  const setAuthToken = (token: string) => {
    cookies.set('f_uauth', token, {
      maxAge: 60 * 60 * 24 * 7,
      path: '/',
    });
  };

  const accountFetcher = useAccountFetcher();

  // Actions
  const loginFan = async (email: string, password: string) => {
    await accountFetcher.login.execute({ email, password });
    if (accountFetcher.login.error.value) {
      return;
    }
    setAuthToken(accountFetcher.login.data.value.token);
    fanStore.setFanGetFetcherData(accountFetcher.login.data.value);
    useTelemetryFetcher().execute({
      metric: 'signin',
      resource: window.location.href,
    });
    router.push({ name: 'Home' });
  };

  const loginMagicLink = async (token: string) => {
    await accountFetcher.magicLink.execute({ token }, token);
    if (accountFetcher.magicLink.error.value || !accountFetcher.magicLink.data.value?.token) {
      console.error('magic link error', accountFetcher.magicLink.error.value);
      // TODO: also remove token from query?
      logoutFan();
      return;
    }
    setAuthToken(accountFetcher.magicLink.data.value.token);
    fanStore.setFanGetFetcherData(accountFetcher.magicLink.data.value);
    router.push({ name: 'Home' });
  };

  const logoutFan = (redirect?: boolean) => {
    cookies.remove('f_uauth', { path: '/' });
    accountFetcher.login.data.value = null;
    accountFetcher.login.error.value = null;
    fanStore.clear();
    if (redirect) router.push({ name: 'Login' });
  };

  const sendSignUp = async (email: string) => {
    await accountFetcher.sendSignUp.execute({
      email,
      returnUrl: `${artistStore.returnUrl}/signup`,
    });
  };

  const signUp = async (password: string) => {
    await accountFetcher.signUp.execute({ password }, tokenInQuery.value);

    if (!accountFetcher.signUp.error.value) {
      useTelemetryFetcher().execute({
        metric: 'signup',
        resource: window.location.href,
      });
    }
  };

  const sendChangeEmail = async (email: string) => {
    await accountFetcher.sendChangeEmail.execute(
      {
        email,
        returnUrl: `${artistStore.returnUrl}/change-email`,
      },
      authToken.value,
    );
  };

  const loginChangeEmail = async (token: string, email: string) => {
    await accountFetcher.changeEmail.execute({ email }, token);
  };

  const sendForgotPassword = async (email: string) => {
    await accountFetcher.sendForgotPassword.execute(
      {
        email,
        returnUrl: `${artistStore.returnUrl}/reset-password`,
      },
      tokenInQuery.value,
    );
  };

  const loginChangePassword = async (password: string) => {
    await accountFetcher.changePassword.execute({ password }, tokenInQuery.value);
  };

  const sendMagicLink = async (email: string) => {
    await accountFetcher.sendMagicLink.execute({
      email,
      returnUrl: `${artistStore.returnUrl}/login`,
    });
  };

  return {
    guestEmail,
    authToken,
    isAuthenticated,
    tokenInQuery,
    setAuthToken,

    loginFanData: accountFetcher.login.data,
    loginFanError: accountFetcher.login.error,
    isLoginFanFetching: accountFetcher.login.isFetching,
    loginFan,
    logoutFan,

    sendMagicLinkData: accountFetcher.sendMagicLink.data,
    sendMagicLinkError: accountFetcher.sendMagicLink.error,
    isSendMagicLinkFetching: accountFetcher.sendMagicLink.isFetching,
    sendMagicLink,
    loginMagicLinkData: accountFetcher.magicLink.data,
    loginMagicLinkError: accountFetcher.magicLink.error,
    isLoginMagicLinkFetching: accountFetcher.magicLink.isFetching,
    loginMagicLink,

    sendForgotPasswordData: accountFetcher.sendForgotPassword.data,
    sendForgotPasswordError: accountFetcher.sendForgotPassword.error,
    isSendForgotPasswordFetching: accountFetcher.sendForgotPassword.isFetching,
    sendForgotPassword,
    loginChangePasswordData: accountFetcher.changePassword.data,
    loginChangePasswordError: accountFetcher.changePassword.error,
    isLoginChangePasswordFetching: accountFetcher.changePassword.isFetching,
    loginChangePassword,

    sendChangeEmailData: accountFetcher.sendChangeEmail.data,
    sendChangeEmailError: accountFetcher.sendChangeEmail.error,
    isSendChangeEmailFetching: accountFetcher.sendChangeEmail.isFetching,
    sendChangeEmail,
    loginChangeEmailData: accountFetcher.changeEmail.data,
    loginChangeEmailError: accountFetcher.changeEmail.error,
    isLoginChangeEmailFetching: accountFetcher.changeEmail.isFetching,
    loginChangeEmail,

    sendSignUpData: accountFetcher.sendSignUp.data,
    sendSignUpError: accountFetcher.sendSignUp.error,
    isSendSignUpFetching: accountFetcher.sendSignUp.isFetching,
    sendSignUp,
    signUpData: accountFetcher.signUp.data,
    signUpError: accountFetcher.signUp.error,
    isSignUpFetching: accountFetcher.signUp.isFetching,
    signUp,
  };
});
